const DESKTOP_MEDIA_QUERY = '(min-width: 1024px)';
const MOBILE_MEDIA_QUERY = '(max-width: 1023px)';
const GRID_COLUMN_GAP = '10px';
const FORM_SELECTOR = 'form[data-testid="form"]';
// The terms field name varies by store and form ("custom#termsandconditions",
// "custom#termsandconditionscheck", "custom#terms_conditions"), so match the
// shared "custom#terms" prefix.
const TERMS_FIELDSET_SELECTOR = 'fieldset:has([name^="custom#terms"])';
const SUBMIT_BUTTON_SELECTOR = 'button[data-testid="btn-form-submit"]';
const RADIO_FIELDSET_SELECTOR = 'fieldset[name="custom#preferred_reply_method"]';
const FILE_INPUT_SELECTOR = '[id="custom#attach_a_file_optional-field"]';
const FILE_BLOCK_SELECTOR = `${FORM_SELECTOR} > div:has(${FILE_INPUT_SELECTOR})`;

// Which form an embed is comes from the data-form-kind attribute the Liquid
// section sets on the embed wrapper, not from the store-specific Shopify Forms
// app id (which differs between environments).
const CONTACT_FORM_KIND = 'contact';
const TRADE_FORM_KIND = 'trade';

const CONTACT_GRID_FIELD_SELECTORS = [
  '[id="first_name"]',
  '[id="last_name"]',
  '[id="email"]',
  '[id="phone_number"]',
  '[id="custom#postcode"]',
  '[id="custom#order_number"]'
];

// The Terms/Privacy URLs come from section settings via data-* on the embed
// wrapper; these patterns locate the phrases in the app's label to linkify.
const TERMS_LINK_PATTERNS = [
  { pattern: /Terms\s*&\s*Conditions/, urlKey: 'termsUrl' },
  { pattern: /Privacy\s*Policy/, urlKey: 'privacyUrl' }
];


const customisedShadowRoots = new WeakSet();
// Per-embed shadow-DOM observers, so they can be disconnected when an embed is
// removed. The Set tracks live embeds for iteration (a WeakMap alone isn't
// enumerable); the WeakMap holds the observer without pinning the element.
const shadowObservers = new WeakMap();
const observedEmbedEls = new Set();
let isObservingDocument = false;

// The Shopify Forms embed renders in an open shadow root, so theme CSS can't
// reach its internals. We adopt stylesheets into the shadow root, keyed off
// the app's stable element ids/names rather than its hashed class names. If
// the app's markup changes, the form gracefully falls back to its defaults.
// Layout differs per form, so each embed gets shared rules plus a
// form-specific sheet resolved from its data-forms-id wrapper.
const buildSharedStyles = () => `
  /* The terms fieldset legend is not part of the design */
  ${TERMS_FIELDSET_SELECTOR} legend {
    display: none;
  }

  ${TERMS_FIELDSET_SELECTOR} a {
    color: var(--color-terracotta);
    text-decoration: underline;
  }

  ${TERMS_FIELDSET_SELECTOR} a:hover {
    text-decoration: none;
  }
`;

const buildContactFormStyles = () => {
  const fieldContainerSelectors = CONTACT_GRID_FIELD_SELECTORS
    .map(selector => `${FORM_SELECTOR} > div:has(${selector})`)
    .join(',\n');

  return `
    /* The app draws field borders as a 0.5px box-shadow, which drops out on the
       textarea's rounded corners — use a real border like the native styling */
    ${FORM_SELECTOR} textarea {
      box-shadow: none;
      border: 1px solid var(--color-soft-grey);
    }

    ${FORM_SELECTOR} textarea:focus {
      border-color: var(--color-charcoal);
      outline: none;
    }

    /* Textarea label per design: bold, above the field instead of the app's
       floating-label overlay (whose background strip also masked the
       textarea's top border). Static positioning makes the app's inline
       "top" style and float transform irrelevant. */
    ${FORM_SELECTOR} > div:has(> textarea) {
      display: flex;
      flex-direction: column;
      gap: 20px;
    }

    ${FORM_SELECTOR} > div:has(> textarea) > label {
      position: static;
      order: -1;
      transform: none;
      background: transparent;
      padding: 0;
      font-weight: 700;
      color: var(--color-dark-grey);
    }

    /* The app draws floating-label mask strips as pseudo-elements on the
       field container and its label — stray artefacts once the label is
       static above the field, so remove them all */
    ${FORM_SELECTOR} > div:has(> textarea)::before,
    ${FORM_SELECTOR} > div:has(> textarea)::after,
    ${FORM_SELECTOR} > div:has(> textarea) > label::before,
    ${FORM_SELECTOR} > div:has(> textarea) > label::after {
      content: none;
    }

    ${FORM_SELECTOR} textarea::placeholder {
      color: var(--color-dark-grey);
    }

    /* Radio row per design: bold label and options on one line. Floating the
       legend opts it out of fieldset's special legend layout; the options div
       is a flex container (a BFC), so it sits beside the float. */
    ${RADIO_FIELDSET_SELECTOR} legend {
      float: left;
      margin: 0 20px 0 0;
      font-weight: 700;
      color: var(--color-dark-grey);
    }

    ${RADIO_FIELDSET_SELECTOR} > div {
      display: flex;
      align-items: center;
      gap: 20px;
    }

    /* File upload per design: bold label, dashed pill button, format caption,
       everything packed to the left (the app spreads and stretches its row) */
    ${FILE_BLOCK_SELECTOR} {
      display: flex;
      justify-content: flex-start;
      align-items: center;
      gap: 20px;
    }

    ${FILE_BLOCK_SELECTOR} > p {
      flex: 0 0 auto;
      margin: 0;
      font-weight: 700;
      color: var(--color-dark-grey);
    }

    ${FILE_BLOCK_SELECTOR} > div {
      flex: 0 0 auto;
      width: auto;
      border: 0;
      box-shadow: none;
      background: transparent;
      padding: 0;
      margin: 0;
    }

    ${FILE_BLOCK_SELECTOR} label + div {
      display: flex;
      align-items: center;
      gap: 10px;
      padding: 0;
      margin: 0;
      min-height: 0;
    }

    ${FILE_BLOCK_SELECTOR} label + div span {
      display: inline-flex;
      align-items: center;
      height: 45px;
      padding: 0 29px;
      border: 1px dashed var(--color-soft-grey);
      border-radius: 9999px;
      background-color: var(--color-greige);
      color: var(--color-dark-grey);
      cursor: pointer;
    }

    /* Replace the app's "Add file" copy visually — its text node is
       React-managed, so rewriting it in JS breaks the filename swap after a
       file is picked. Hide the text and draw ours with pseudo-elements; when
       a file is chosen the span's class changes and the real filename shows. */
    ${FILE_BLOCK_SELECTOR} span[class*="_formFileInputButton_"] {
      font-size: 0;
    }

    ${FILE_BLOCK_SELECTOR} span[class*="_formFileInputButton_"]::before {
      content: '+';
      margin-right: 10px;
      font-size: var(--forms-base-font-size, 14px);
    }

    ${FILE_BLOCK_SELECTOR} span[class*="_formFileInputButton_"]::after {
      content: 'Choose a file';
      font-size: var(--forms-base-font-size, 14px);
    }

    ${FILE_BLOCK_SELECTOR} span[class*="_formFileInputFileName_"] {
      color: var(--color-charcoal);
    }

    @media ${DESKTOP_MEDIA_QUERY} {
      ${FORM_SELECTOR} {
        display: grid;
        grid-template-columns: repeat(2, minmax(0, 1fr));
        column-gap: ${GRID_COLUMN_GAP};
        align-items: center;
      }

      ${FORM_SELECTOR} > * {
        grid-column: 1 / -1;
      }

      ${fieldContainerSelectors} {
        grid-column: auto;
      }

      /* Bottom row per design: terms checkbox left, submit button right */
      ${FORM_SELECTOR} > ${TERMS_FIELDSET_SELECTOR} {
        grid-column: 1;
        margin: 0;
      }

      ${FORM_SELECTOR} > ${SUBMIT_BUTTON_SELECTOR} {
        grid-column: 2;
        justify-self: end;
        width: fit-content;
        padding-left: 70px;
        padding-right: 70px;
      }
    }

    @media ${MOBILE_MEDIA_QUERY} {
      /* Radio row wraps: label on its own line, options on the next. Dropping
         the legend float lets the options div flow below it. */
      ${RADIO_FIELDSET_SELECTOR} legend {
        float: none;
        margin: 0 0 10px 0;
      }

      /* File upload stacks: "Attach a file" label above, input below. */
      ${FILE_BLOCK_SELECTOR} {
        flex-direction: column;
        align-items: flex-start;
        gap: 10px;
      }
    }
  `;
};

const buildTradeFormStyles = () => `
  /* Trade enquiry layout: first/last name share a row at every width (the
     design's single "Contact name" field maps to the app's two name fields);
     email, company, and phone each span a full row — the phone field's
     country selector leaves too little room for the number in a half column. */
  ${FORM_SELECTOR} {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    column-gap: ${GRID_COLUMN_GAP};
  }

  ${FORM_SELECTOR} > * {
    grid-column: 1 / -1;
  }

  ${FORM_SELECTOR} > div:has([id="first_name"]),
  ${FORM_SELECTOR} > div:has([id="last_name"]) {
    grid-column: auto;
  }
`;

const FORM_STYLE_BUILDERS = {
  [CONTACT_FORM_KIND]: buildContactFormStyles,
  [TRADE_FORM_KIND]: buildTradeFormStyles
};

// Reads the data-* the Liquid section sets on the embed wrapper: the form kind,
// the Terms/Privacy URLs, and the localised strings the Forms app can't set
// (file caption, textarea placeholder). Returns nulls/empties if absent.
const getEmbedConfig = (embedEl) => {
  const wrapperEl = embedEl.closest('[data-shopify-forms-embed]');
  return {
    kind: wrapperEl?.dataset.formKind || null,
    termsUrl: wrapperEl?.dataset.termsUrl || '',
    privacyUrl: wrapperEl?.dataset.privacyUrl || '',
    fileCaption: wrapperEl?.dataset.fileCaption || '',
    textareaPlaceholder: wrapperEl?.dataset.textareaPlaceholder || ''
  };
};

// Wraps the matched Terms/Privacy phrases in links. Rebuilds the label with the
// DOM API (text nodes + anchor elements) rather than a textContent -> innerHTML
// round-trip, so any &, <, > in the app's label text stays literal text.
const linkifyTermsLabel = (shadowRoot, config) => {
  const labelTextEl = shadowRoot.querySelector(`${TERMS_FIELDSET_SELECTOR} label span`);
  if (!labelTextEl || labelTextEl.querySelector('a')) return;

  const links = TERMS_LINK_PATTERNS
    .map(({ pattern, urlKey }) => ({ pattern, url: config[urlKey] }))
    .filter(({ url }) => url);
  const labelText = labelTextEl.textContent;
  if (!links.some(({ pattern }) => pattern.test(labelText))) return;

  // Find each phrase's position, then rebuild the label in order: plain text
  // between matches as text nodes, matched phrases as anchors.
  const matches = links
    .map(({ pattern, url }) => {
      const match = pattern.exec(labelText);
      return match ? { start: match.index, end: match.index + match[0].length, text: match[0], url } : null;
    })
    .filter(Boolean)
    .sort((a, b) => a.start - b.start);
  if (matches.length === 0) return;

  const fragment = document.createDocumentFragment();
  let cursor = 0;
  matches.forEach(({ start, end, text, url }) => {
    if (start < cursor) return;
    if (start > cursor) {
      fragment.append(document.createTextNode(labelText.slice(cursor, start)));
    }
    const anchorEl = document.createElement('a');
    anchorEl.href = url;
    anchorEl.target = '_blank';
    anchorEl.rel = 'noopener noreferrer';
    anchorEl.textContent = text;
    fragment.append(anchorEl);
    cursor = end;
  });
  if (cursor < labelText.length) {
    fragment.append(document.createTextNode(labelText.slice(cursor)));
  }

  labelTextEl.replaceChildren(fragment);
};

const appendFileUploadCaption = (shadowRoot, captionText) => {
  if (!captionText) return;
  const fileInputEl = shadowRoot.querySelector(FILE_INPUT_SELECTOR);
  const fileBlockEl = fileInputEl?.closest('form > div');
  if (!fileBlockEl || fileBlockEl.querySelector('[data-file-upload-caption]')) return;

  const captionEl = document.createElement('span');
  captionEl.setAttribute('data-file-upload-caption', '');
  captionEl.textContent = captionText;
  fileBlockEl.append(captionEl);
};

const setTextareaPlaceholder = (shadowRoot, placeholder) => {
  if (!placeholder) return;
  const textareaEl = shadowRoot.querySelector(`${FORM_SELECTOR} textarea`);
  if (!textareaEl || textareaEl.placeholder) return;
  textareaEl.placeholder = placeholder;
};

const applyShadowDomContentTweaks = (shadowRoot, config) => {
  linkifyTermsLabel(shadowRoot, config);
  appendFileUploadCaption(shadowRoot, config.fileCaption);
  setTextareaPlaceholder(shadowRoot, config.textareaPlaceholder);
};

const adoptStyles = (shadowRoot, css) => {
  const sheet = new CSSStyleSheet();
  sheet.replaceSync(css);
  shadowRoot.adoptedStyleSheets = [...shadowRoot.adoptedStyleSheets, sheet];
};

const customiseFormsEmbed = (embedEl) => {
  const shadowRoot = embedEl.shadowRoot;
  if (!shadowRoot) return;

  const config = getEmbedConfig(embedEl);

  if (!customisedShadowRoots.has(shadowRoot)) {
    customisedShadowRoots.add(shadowRoot);

    adoptStyles(shadowRoot, buildSharedStyles());
    const buildFormStyles = FORM_STYLE_BUILDERS[config.kind];
    if (buildFormStyles) adoptStyles(shadowRoot, buildFormStyles());

    // The app renders (and can re-render) its form asynchronously inside the
    // shadow root; re-run the idempotent content tweaks when that happens.
    // Keyed by embedEl so it can be disconnected once the embed leaves the DOM.
    const shadowObserver = new MutationObserver(() => applyShadowDomContentTweaks(shadowRoot, config));
    shadowObserver.observe(shadowRoot, { childList: true, subtree: true });
    shadowObservers.set(embedEl, shadowObserver);
  }

  applyShadowDomContentTweaks(shadowRoot, config);
};

// Disconnect the per-embed observer for any embed no longer in the document
// (e.g. a section reload in the theme editor), so it and its closure can be GC'd.
const disconnectDetachedObservers = (liveEmbedEls) => {
  observedEmbedEls.forEach(embedEl => {
    if (liveEmbedEls.includes(embedEl)) return;
    shadowObservers.get(embedEl)?.disconnect();
    shadowObservers.delete(embedEl);
    observedEmbedEls.delete(embedEl);
  });
};

const applyFormsEmbedCustomisations = () => {
  const embedEls = [...document.querySelectorAll('shopify-forms-embed')];
  disconnectDetachedObservers(embedEls);
  embedEls.forEach(embedEl => {
    observedEmbedEls.add(embedEl);
    customiseFormsEmbed(embedEl);
  });
};

const initShopifyForms = () => {
  applyFormsEmbedCustomisations();

  if (isObservingDocument) return;
  isObservingDocument = true;

  // The Forms app mounts its embed after our bundle runs; watch for it.
  const documentObserver = new MutationObserver(applyFormsEmbedCustomisations);
  documentObserver.observe(document.body, { childList: true, subtree: true });
};

export default initShopifyForms;
